import React from "react";
import { useTranslation } from "react-i18next";
import StatusBadge from "../../../../components/StatusBadge";
import { ArrowPathIcon, EyeIcon, ChartBarIcon } from "@heroicons/react/24/solid";
import SortIcon from "../../../../components/SortIcon";
import ArrayStatusBadge from "../../../../components/ArrayStatusBadge";
import { useMutation, useQueryClient } from "react-query";
import IconButton from "../../../../components/IconButton";
import IconButtonLink from "../../../../components/IconButtonLink";
import { apollo } from "../../../../utils/apollo";
import { RECHECK_MEMBER_STATUS } from "../../../../utils/graphql";
import { toast } from "react-toastify";
import { cn } from "../../../../utils/style";

function CompactPolicyList({ handleChangeSort, records, sort, hideBatchButton = false }) {
  const { t } = useTranslation();

  const tableHeader = [
    {
      column: "policy_details",
      title: t("policy.table.policy_details"),
      sortable: false,
    },
    {
      column: "integration_details",
      title: t("policy.table.integration_details"),
      sortable: false,
    },
    {
      column: "policy_status",
      title: t("policy.table.policy_status"),
      sortable: false,
    },
    {
      column: "integration_status",
      title: t("policy.table.integration_status"),
      sortable: false,
    },
    // {column: 'amount_details', title: t('policy.table.amount_details'), sortable: false},
    {
      column: "view",
      title: t("policy.table.view"),
      sortable: false,
      hidden: true,
    },
  ];

  return (
    <div className="layer-base mt-8">
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full align-middle pb-10">
          <table className="min-w-full divide-y divide-slate-100">
            <thead>
              <tr className="bg-slate-50/50">
                {tableHeader?.map((item) => (
                  <th
                    key={item.column}
                    scope="col"
                    className="px-6 py-4 text-start text-[10px] font-black uppercase tracking-widest text-slate-500"
                  >
                    {!item.hidden && (
                      <>
                        {item.sortable ? (
                          <button
                            onClick={() => handleChangeSort(item.column)}
                            type="button"
                            className="group inline-flex items-center"
                          >
                            {item.title}
                            <SortIcon sort={sort} field={item.column} />
                          </button>
                        ) : (
                          <span>{item.title}</span>
                        )}
                      </>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {records?.data?.map((record) => {
                const isShafafiya =
                  record?.integration?.integrationType === "shafafiya";
                const policiable = record?.policiable || {};
                return (
                  <tr
                    key={record?.policyUuid}
                    className="hover:bg-slate-50/50 transition-colors duration-200"
                  >
                    {/* Column 1: Primary Identification */}
                    <td className="whitespace-nowrap align-top py-5 px-6 text-[11px] text-slate-600">
                      {isShafafiya ? (
                        <div className="space-y-1.5">
                          <Row
                            title={`Shafafiya Ref`}
                            value={record.policyNumber}
                          />
                          <Row
                            title={`Emirates ID`}
                            value={policiable.members?.[0]?.eid}
                          />
                          <Row
                            title={`ID`}
                            value={record.policyUuid}
                            valueClassName="opacity-40 font-medium"
                          />
                        </div>
                      ) : (
                        <div className="space-y-1.5">
                          <Row
                            title={`Policy Number`}
                            value={
                              policiable.policyNumber || record.policyNumber
                            }
                          />
                          <Row
                            title={`Plan Name`}
                            value={policiable.planName}
                          />
                          <Row
                            title={`ID`}
                            value={record.policyUuid}
                            valueClassName="opacity-40 font-medium"
                          />
                        </div>
                      )}
                    </td>

                    {/* Column 2: Contextual Details */}
                    <td className="whitespace-nowrap align-top py-5 px-6 text-[11px] text-slate-600">
                      {isShafafiya ? (
                        <div className="space-y-1.5">
                          <Row
                            title={`Validity Date`}
                            value={
                              policiable.validityDate
                                ?.split(" ")[0]
                                ?.split("T")[0]
                            }
                          />
                          <Row
                            title={`Total Penalty`}
                            value={
                              policiable.totalPenalty !== null &&
                              policiable.totalPenalty !== undefined
                                ? `${policiable.totalPenalty} ${t("AED")}`
                                : null
                            }
                          />
                          <Row
                            title={`Created`}
                            value={
                              record.fromApi ? t("API") : t("Dashboard")
                            }
                            valueClassName="opacity-40 font-medium"
                          />
                        </div>
                      ) : (
                        <div className="space-y-1.5">
                          <Row
                            title={`Dates`}
                            value={
                              policiable.policyStartDate
                                ? `${policiable.policyStartDate?.split(" ")[0]?.split("T")[0]} - ${policiable.policyExpiryDate?.split(" ")[0]?.split("T")[0]}`
                                : null
                            }
                          />
                          <Row
                            title={`IC Request Ref`}
                            value={policiable.icRequestRefNo}
                          />
                          <Row
                            title={`Created`}
                            value={
                              record.fromApi ? t("API") : t("Dashboard")
                            }
                            valueClassName="opacity-40 font-medium"
                          />
                        </div>
                      )}
                    </td>

                    {/* Column 3: Status */}
                    <td className="whitespace-nowrap align-top py-5 px-6 text-[11px] text-slate-600">
                      <div className="mb-3">
                        <StatusBadge status={record?.status} />
                      </div>
                      <div className="text-[10px] opacity-60">
                        <Row
                          title={`Integration`}
                          value={record.integration?.name}
                          valueClassName="truncate max-w-[120px] font-bold text-primary-900"
                        />
                      </div>
                    </td>

                    {/* Column 4: Detailed Integration Status */}
                    <td className="whitespace-nowrap align-top py-5 px-6 text-[11px] text-slate-600">
                      {!isShafafiya && (
                        <div className="mb-1.5">
                          <Row
                            title={`Policy`}
                            value={
                              <StatusBadge
                                h="h-3"
                                w="w-3"
                                status={record?.policyStatus}
                                boolean={true}
                              />
                            }
                          />
                        </div>
                      )}
                      <Row
                        title={`Members`}
                        value={
                          <ArrayStatusBadge
                            h="h-3"
                            w="w-3"
                            statusList={record?.policyMembersStatus}
                            boolean={true}
                          />
                        }
                      />
                    </td>

                    <td className="relative whitespace-nowrap py-5 px-6 text-end text-sm">
                      <div className="flex gap-3 items-center justify-end">
                        <Actions record={record} hideBatchButton={hideBatchButton} />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Row({ title, value, valueClassName }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1.5">
      <div className="font-bold text-slate-400 uppercase text-[9px] tracking-wider">
        {t(title)}
        {":"}
      </div>
      {value ? (
        <div className={cn("font-bold text-slate-700", valueClassName)}>
          {value}
        </div>
      ) : (
        <div className="opacity-40 font-medium">{t("N/A")}</div>
      )}
    </div>
  );
}

function Actions({ record, hideBatchButton }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const reCheckMemberStatusMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apollo.mutate({
        mutation: RECHECK_MEMBER_STATUS,
        variables: {
          policyUuid: record?.policyUuid,
        },
      });
      return data?.reCheckMemberStatus;
    },
    onError: (error) => {
      toast.error(t("Recheck operation failed"));
      console.error(error);
    },
    onSuccess: async (data) => {
      if (data) {
        toast.success(t("Recheck operation started"));
      } else {
        toast.error(t("Recheck operation failed"));
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(["policy_list"]);
    },
  });

  return (
    <>
      {record?.fileImport?.fileImportUuid && !hideBatchButton && (
        <IconButtonLink
          href={`/policy/import/details/${record.fileImport.fileImportUuid}`}
          title={t("View Batch Details")}
        >
          <ChartBarIcon className="h-5 w-5 text-primary-500" />
        </IconButtonLink>
      )}
      <IconButtonLink
        href={`/policy/view/${record?.policyUuid}`}
        title={t("View Details")}
      >
        <EyeIcon className="h-5 w-5 text-gray-600" />
      </IconButtonLink>
      <IconButton
        onClick={() => reCheckMemberStatusMutation.mutate()}
        title={t("Re-check Status")}
      >
        <ArrowPathIcon
          className={`h-5 w-5 text-gray-600 ${
            reCheckMemberStatusMutation.isLoading ? "animate-spin" : ""
          }`}
        />
      </IconButton>
    </>
  );
}

export default CompactPolicyList;
